// /join/:token — where a plan's invite link lands. Before sign-in it says who invited you to which
// plan, who's in it and what it's about (GET /invites/{token}, public: the link is the secret);
// signed in, one button joins. Signing in comes straight back here (authConfig.js
// signInWithRedirect), so a new person can create an account and finish joining without finding
// the link again. Once joined, the next step is the iPhone app, where the plan is waiting.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import AppleIcon from "@mui/icons-material/Apple";
import { signInWithRedirect, cachedAccount, accountEmail } from "../authConfig";
import MemoryNav from "../components/MemoryNav";
import { C, CARD, MICRO_LABEL } from "../components/memoryTheme";
import { getInvitePreview, acceptInvite } from "../utils/api";

export const APP_STORE_URL = "https://apps.apple.com/app/id6822843416";

// The preview's `people` (members' first names) as a sentence: "Alex", "Alex and Sam",
// "Alex, Sam and Jo". Older backends don't send it; anything unusable reads as nobody.
const MAX_NAMES = 6;
export function peopleLine(people) {
  const list = (Array.isArray(people) ? people : typeof people === "string" ? [people] : [])
    .map((n) => (typeof n === "string" ? n.trim() : ""))
    .filter(Boolean);
  if (!list.length) return "";
  if (list.length > MAX_NAMES) return `${list.slice(0, MAX_NAMES).join(", ")} and others`;
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

const ENDED = {
  accepted: "This invite has already been used. Ask for a new one.",
  withdrawn: "This invite was withdrawn.",
  expired: "This invite has expired. Ask for a new one.",
  invalid: "This invite link isn't valid.",
};

export default function Join() {
  const { token } = useParams();
  const { instance } = useMsal();
  const isAuth = useIsAuthenticated();
  const [invite, setInvite] = useState(null);     // the preview
  const [state, setState] = useState("loading");  // loading | ready | ended | joined | error
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "Join a plan · Assertion";
    return () => { document.title = "Assertion AI Studio"; };
  }, []);

  useEffect(() => {
    getInvitePreview(token)
      .then((d) => { setInvite(d); setState(d.status === "open" ? "ready" : "ended"); })
      .catch((e) => { setInvite({ status: e?.response?.status === 404 ? "invalid" : "" }); setState(e?.response?.status === 404 ? "ended" : "error"); });
  }, [token]);

  const join = async () => {
    setBusy(true); setErr("");
    try { setJoined(await acceptInvite(token)); setState("joined"); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || "Could not join."); }
    finally { setBusy(false); }
  };

  const who = invite?.inviter || "Someone";
  const name = invite?.space_name || "";
  const people = peopleLine(invite?.people);
  const summary = typeof invite?.summary === "string" ? invite.summary.trim() : "";
  const primary = { mt: 2.5, bgcolor: C.teal, textTransform: "none", fontWeight: 700, py: 1.1, boxShadow: "none", "&:hover": { bgcolor: C.tealDeep } };
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: C.canvas }}>
      <MemoryNav />
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", px: 2, py: { xs: 4, md: 8 } }}>
        <Box sx={{ ...CARD, p: { xs: 2.5, md: 3.5 }, width: "100%", maxWidth: 460, alignSelf: "flex-start" }}>
          {state === "loading" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={16} sx={{ color: C.teal }} />
              <Typography sx={{ fontSize: 13, color: C.muted }}>Opening the invite…</Typography>
            </Box>
          )}
          {state === "error" && <Typography sx={{ fontSize: 13.5, color: C.body }}>Couldn't open this invite right now. Try again in a moment.</Typography>}
          {state === "ended" && (
            <>
              <Typography sx={MICRO_LABEL}>Invite</Typography>
              <Typography sx={{ fontSize: 16, color: C.ink, fontWeight: 600, mt: 0.75 }}>{ENDED[invite?.status] || ENDED.invalid}</Typography>
            </>
          )}
          {state === "ready" && (
            <>
              <Typography sx={MICRO_LABEL}>{who} invited you to plan</Typography>
              <Typography component="h1" sx={{ fontSize: 26, color: C.ink, fontWeight: 700, mt: 0.75, lineHeight: 1.25, textWrap: "balance" }}>
                {name}
              </Typography>
              {people && (
                <Typography sx={{ fontSize: 14, color: C.body, mt: 1 }}>With {people}.</Typography>
              )}
              {summary && (
                <Box component="blockquote" data-testid="plan-summary"
                     sx={{ m: 0, mt: 2, py: 1.25, px: 1.75, bgcolor: C.canvas, borderLeft: `3px solid ${C.line}`, borderRadius: "0 6px 6px 0", color: C.muted, fontSize: 13.5, lineHeight: 1.6,
                           whiteSpace: "pre-line", display: "-webkit-box", WebkitLineClamp: 8, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {summary}
                </Box>
              )}
              {isAuth ? (
                <>
                  <Button variant="contained" onClick={join} disabled={busy} fullWidth sx={primary}>
                    {busy ? "Joining…" : `Join ${name}`}
                  </Button>
                  <Typography sx={{ fontSize: 12, color: C.muted, mt: 1 }}>
                    Joining as {accountEmail(cachedAccount(instance)) || "your Assertion account"}
                  </Typography>
                </>
              ) : (
                <Button variant="contained" onClick={async () => { try { await signInWithRedirect(instance); } catch (e) { setErr(e?.message || "Sign-in failed."); } }} fullWidth
                        sx={primary}>
                  Join with a free account
                </Button>
              )}
              {err && <Typography sx={{ fontSize: 12.5, color: "#b42318", mt: 1.25 }}>{err}</Typography>}
            </>
          )}
          {state === "joined" && (
            <>
              <Typography component="h1" sx={{ fontSize: 22, color: C.ink, fontWeight: 700, lineHeight: 1.3, textWrap: "balance" }}>
                You're in {joined?.name || name}
              </Typography>
              <Typography sx={{ fontSize: 14, color: C.body, mt: 1.25, lineHeight: 1.6 }}>
                Get the app to talk it through. Sign in with the same account and the plan is waiting.
              </Typography>
              <Button variant="contained" href={APP_STORE_URL} target="_blank" rel="noopener" fullWidth
                      startIcon={<AppleIcon />}
                      sx={{ mt: 2.25, bgcolor: "#000", color: "#fff", textTransform: "none", fontWeight: 600, py: 1.1, boxShadow: "none", "&:hover": { bgcolor: "#222", boxShadow: "none" } }}>
                Download on the App Store
              </Button>
              <Link href={`/memory?space=${encodeURIComponent(joined?.workspace || "")}`} underline="hover"
                    sx={{ display: "inline-block", mt: 1.5, fontSize: 12.5, color: C.muted }}>
                Open it in Memory
              </Link>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
